import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { Add, Close, Edit } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import {
  ConfirmDialog,
  WrappedTableCell
} from '@sierralabs/core-ui';
import { EntitySchema, getEntityProperties } from '@sierralabs/entity-core';
import {
  EntitySchemaPicker,
  EntitySchemaRelationPicker,
  useEntityContext
} from '@sierralabs/entity-ui';
import { State, Workflow, WorkflowInvoke } from '@sierralabs/workflow';
import { parseTaskStatus } from '@sierralabs/workflow-core';
import {
  ErrorMetadataPlugin,
  ErrorMetadataPluginProps,
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin,
  TaskContext,
  useFindTaskTypeById
} from '@sierralabs/workflow-ui';

import TaskTypeSelector from '../../../task-types/components/TaskTypeSelector';
import { useGetTaskTypes } from '../../../task-types/TaskType.hooks';
import { TaskType } from '../../../task-types/TaskType.interface';
import {
  ErrorPluginView,
  UpdateRelatedEntityStatusErrorItem
} from '../../plugins/services/UpdateRelatedEntityStatusPlugin';
import { useFindWorkflowVersionByTaskType } from '../../Workflow.hooks';
import { WorkflowVersion } from '../../Workflow.interface';

export interface UpdateRelatedEntityStatusLegacyParams {
  src: 'updateRelatedEntityStatusLegacy';
  data: {
    entity: { id?: string; table: string }; // entitySchema id and tableName
    relations: {
      name?: string; // user friendly display name
      // join relations; i.e. propertyName[0]->...propertyName[n]
      propertyNames: string[];
      taskType: string; // taskType.name
      events: {
        state: string; // stateNode.name
        event: string; // event.name
        target: string; // transition.target
      }[];
    }[];
  };
}

type Relation =
  UpdateRelatedEntityStatusLegacyParams['data']['relations'][number];
type RelationEvent = Relation['events'][number];

function validateRelation(value: Partial<Relation>): value is Relation {
  return !!(
    value.taskType &&
    Array.isArray(value.propertyNames) &&
    value.propertyNames.length > 0 &&
    value.propertyNames.every(propertyName => !!propertyName) &&
    Array.isArray(value.events) &&
    value.events.length > 0
  );
}

const UpdateRelatedEntityStatusLegacyConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { taskTypeId, onChange } = props;
  const [open, setOpen] = useState(false);
  const [invoke, setInvoke] = useState(props.invoke);
  const [updateIndex, setUpdateIndex] = useState(-1);
  const [removeIndex, setRemoveIndex] = useState(-1);
  const { entities } = useEntityContext();
  if (!invoke.data) invoke.data = {};
  const data: UpdateRelatedEntityStatusLegacyParams['data'] = invoke.data;
  const relations = data.relations || [];
  const { data: taskType, get } = useFindTaskTypeById(taskTypeId || 0);
  // entity schema of current Workflow
  const schema = taskType
    ? entities?.find(schema => {
        return taskType.entityId
          ? schema.id === taskType.entityId
          : schema.tableName === taskType.entityTable;
      })
    : undefined;

  const update = (data: UpdateRelatedEntityStatusLegacyParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  const getPropertyDisplay = (relation: Relation | undefined) => {
    if (!relation) {
      return '?';
    }
    const properties = getEntityProperties(
      relation.propertyNames,
      schema,
      entities || []
    );
    const property = properties
      .map(property => {
        return property.relation?.displayName || property.propertyName || '?';
      })
      .join(' -> ');
    const entity = schema?.displayName || '?';
    return `${entity} -> ${property}`;
  };

  useEffect(() => {
    get();
  }, [get]);

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Related Entity</TableCell>
              <TableCell>Transitions</TableCell>
              <TableCell style={{ width: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {relations.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No relations added.</TableCell>
              </TableRow>
            )}
            {relations.map((relation, index) => (
              <TableRow key={index} className='dense'>
                <WrappedTableCell>
                  {relation.name || (
                    <Typography variant='body2' color='textSecondary'>
                      Untitled
                    </Typography>
                  )}
                </WrappedTableCell>
                <WrappedTableCell>
                  {getPropertyDisplay(relation)}
                </WrappedTableCell>
                <WrappedTableCell>
                  {relation.events
                    .map(event => {
                      const target = parseTaskStatus(event.target);
                      return `${event.state} -> ${target}`;
                    })
                    .join(', ')}
                </WrappedTableCell>
                <TableCell padding='checkbox'>
                  <IconButton
                    size='small'
                    onClick={() => setUpdateIndex(index)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => setRemoveIndex(index)}
                  >
                    <Close />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2}>
        <Button
          variant='outlined'
          startIcon={<Add />}
          onClick={() => setOpen(true)}
        >
          Add Relation
        </Button>
      </Box>

      {!schema && (
        <Box mt={2}>
          <Alert severity='error'>No schema found for this Workflow.</Alert>
        </Box>
      )}

      {schema && (
        <AddRelationDialog
          schema={schema}
          open={open || updateIndex > -1}
          relation={relations[updateIndex]}
          title={updateIndex > -1 ? 'Update Relation' : 'Add Relation'}
          onCancel={() => {
            setOpen(false);
            setUpdateIndex(-1);
          }}
          onSubmit={relation => {
            data.entity = { id: schema.id, table: schema.tableName };
            if (!data.relations) {
              data.relations = [];
            }
            if (updateIndex > -1) {
              data.relations.splice(updateIndex, 1, relation);
            } else {
              data.relations.push(relation);
            }
            setOpen(false);
            setUpdateIndex(-1);
            update({ ...data });
          }}
        />
      )}

      <ConfirmDialog
        confirmLabel='Remove'
        open={removeIndex > -1}
        title='Remove Relation'
        message={`Are you sure you want to remove ${
          relations[removeIndex]?.name || 'this'
        } relation?`}
        onCancel={() => setRemoveIndex(-1)}
        onConfirm={() => {
          if (!data.relations) {
            data.relations = [];
          }
          if (removeIndex > -1) {
            data.relations.splice(removeIndex, 1);
          }
          setRemoveIndex(-1);
          update({ ...data });
        }}
      />
    </>
  );
};

const StyledBox = styled(Box)`
  .grid-field {
    flex: 1;
  }
  .grid-action {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
  }
`;

interface AddRelationDialogProps {
  open: boolean;
  title: string;
  schema: EntitySchema;
  relation?: Relation;
  onCancel?: () => void;
  onSubmit?: (relation: Relation) => void;
}

const AddRelationDialog: React.FC<AddRelationDialogProps> = props => {
  const {
    open,
    title,
    schema,
    relation: relationProp,
    onCancel,
    onSubmit
  } = props;
  const [relation, setRelation] = useState<Partial<Relation>>(
    () => relationProp || {}
  );
  const [workflowVersion, setWorkflowVersion] = useState<WorkflowVersion>();
  const { entities } = useEntityContext();
  const [{ data, isLoading: isFinding }, getTaskTypes] = useGetTaskTypes();
  const {
    workflowVersion: foundWorkflowVersion,
    isLoading,
    findWorkflowVersion
  } = useFindWorkflowVersionByTaskType(relation.taskType || '');

  const workflow = useMemo(() => {
    return workflowVersion ? new Workflow(workflowVersion.config) : undefined;
  }, [workflowVersion]);

  const taskTypes = useMemo(() => data || [], [data]);

  const events = useMemo(() => {
    const allEvents: RelationEvent[] = [];
    for (const state of workflow?.stateNode.states || []) {
      for (const event of state.events || []) {
        for (const transition of event.transitions || []) {
          allEvents.push({
            event: event.name || '',
            state: state.name,
            target: transition.target
          });
        }
      }
    }
    return allEvents;
  }, [workflow]);

  const selectedEvents = useMemo(() => {
    const relationEvents = relation.events || [];
    return events.filter(event => {
      return relationEvents.some(relationEvent => {
        return (
          relationEvent.state === event.state &&
          relationEvent.event === event.event &&
          relationEvent.target === event.target
        );
      });
    });
  }, [events, relation]);

  const properties = useMemo(() => {
    return getEntityProperties(relation.propertyNames, schema, entities || []);
  }, [relation, schema, entities]);

  // get last property.relation
  const propertyRelation = useMemo(() => {
    return properties[properties.length - 1]?.relationSchema;
  }, [properties]);
  const entityName = propertyRelation?.name;

  const validRelation = validateRelation(relation) ? relation : undefined;

  useEffect(() => {
    if (entityName) {
      getTaskTypes({ targetEntity: entityName });
    }
  }, [entityName, getTaskTypes]);

  useEffect(() => {
    relation.taskType ? findWorkflowVersion() : setWorkflowVersion(undefined);
  }, [relation.taskType, findWorkflowVersion]);

  useEffect(() => {
    setWorkflowVersion(foundWorkflowVersion);
  }, [foundWorkflowVersion]);

  // clear state
  useEffect(() => {
    if (!open) {
      setRelation({});
      setWorkflowVersion(undefined);
    }
  }, [open]);

  useEffect(() => {
    relationProp && setRelation(relationProp);
  }, [relationProp]);

  return (
    <Dialog fullWidth open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            fullWidth
            variant='outlined'
            label='Display Name'
            placeholder='Enter the display name of the Relation'
            value={relation.name || ''}
            onChange={event => {
              setRelation(relation => {
                return { ...relation, name: event.target.value };
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <EntitySchemaPicker
            disabled
            value={schema.name}
            textFieldProps={{ label: 'Workflow Entity' }}
          />
        </Box>
        {properties.map((property, index) => (
          <StyledBox mt={2} key={`${property.propertyName}-${index}`}>
            <Grid container spacing={0}>
              <Grid item className='grid-field'>
                <EntitySchemaRelationPicker
                  schema={property.schema}
                  value={property.propertyName}
                  textFieldProps={{
                    label: index === 0 ? 'Related Entity' : 'Entity Relation',
                    placeholder: `Select a Relationship from ${
                      property.schema?.displayName || '?'
                    }`
                  }}
                  onChange={relation => {
                    const propertyName = relation?.propertyName;
                    setRelation(relation => {
                      relation = { ...relation };
                      if (!relation.propertyNames) {
                        relation.propertyNames = [];
                      }
                      relation.propertyNames[index] = propertyName || '';
                      if (!propertyName) {
                        relation.events = [];
                        relation.taskType = undefined;
                        // unset all subsequent relations
                        relation.propertyNames.splice(index + 1);
                      }
                      return relation;
                    });
                  }}
                />
              </Grid>
              {index > 0 && (
                <Grid item className='grid-action'>
                  <IconButton
                    onClick={() => {
                      setRelation(relation => {
                        relation = {
                          ...relation,
                          events: [],
                          taskType: undefined
                        };
                        relation.propertyNames?.splice(index);
                        return relation;
                      });
                    }}
                  >
                    <Close />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </StyledBox>
        ))}
        <Box mt={2}>
          <Button
            fullWidth
            variant='outlined'
            startIcon={<Add />}
            // disabled if no selected for last property
            disabled={!properties[properties.length - 1].propertyName}
            onClick={() => {
              setRelation(relation => {
                relation = {
                  ...relation,
                  events: [],
                  taskType: undefined
                };
                if (!relation.propertyNames) {
                  relation.propertyNames = [];
                }
                relation.propertyNames.push('');
                return relation;
              });
            }}
          >
            Add Relation
          </Button>
        </Box>
        <Box mt={2}>
          <TaskTypeSelector
            options={taskTypes}
            isLoading={isFinding}
            value={relation.taskType}
            disabled={!propertyRelation}
            textFieldProps={{
              label: 'Entity Task Type',
              placeholder: 'Select the Task Type of Entity'
            }}
            onChange={(taskType: TaskType | null) => {
              // add display name if not set
              const taskTypeLabel = taskType
                ? `${taskType.label} (${taskType.prefix})`
                : '';
              setRelation(relation => {
                relation = {
                  ...relation,
                  taskType: taskType?.name,
                  name: relation.name || taskTypeLabel
                };
                if (!relation.taskType) {
                  relation.events = [];
                }
                return relation;
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <Autocomplete
            multiple
            options={events}
            loading={isLoading}
            value={selectedEvents}
            disabled={!workflow || !relation.taskType}
            getOptionLabel={option => {
              const { event, state, target } = option;
              return `${event} (${state} -> ${parseTaskStatus(target)})`;
            }}
            onChange={(_, events) => {
              setRelation(relation => ({ ...relation, events }));
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant='outlined'
                label='Event Transitions'
                placeholder='Select Event Transitions'
              />
            )}
          />
        </Box>
        {workflow && (
          <Box mt={2}>
            <Alert severity='info'>
              The <strong>event transitions</strong> will trigger for the
              related entities. The related entities must be in a status that
              can transition to the target status.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!validRelation}
          onClick={() => {
            if (validRelation) {
              onSubmit?.(validRelation);
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
`;

const UpdateRelatedEntityStatusLegacyDisplay: React.FC<
  ServiceDisplayProps
> = props => {
  const { invoke } = props;
  if (!invoke.data) invoke.data = {};
  const data: UpdateRelatedEntityStatusLegacyParams['data'] = invoke.data;
  const relations = data.relations || [];
  return (
    <StyledDisplayDiv>
      {relations.map((relation, index) => {
        const name = relation.name || 'Untitled';
        const { length } = relation.events;
        return (
          <div key={`${name}-${index}`}>
            {name}: {length} transition{length !== 1 && 's'}
          </div>
        );
      })}
    </StyledDisplayDiv>
  );
};

export interface UpdateRelatedEntityStatusLegacyError {
  type: 'updateRelatedEntityStatusLegacy';
  data: { items: UpdateRelatedEntityStatusErrorItem[] };
}

export const UpdateRelatedEntityStatusLegacyErrorPlugin: ErrorMetadataPlugin<UpdateRelatedEntityStatusLegacyError> =
  {
    type: 'updateRelatedEntityStatusLegacy',
    render: ErrorPluginView as unknown as React.FC<
      ErrorMetadataPluginProps<UpdateRelatedEntityStatusLegacyError>
    >
  };

export const UpdateRelatedEntityStatusLegacyPlugin: ServicePlugin<UpdateRelatedEntityStatusLegacyParams> =
  {
    src: 'updateRelatedEntityStatusLegacy',
    name: 'Update Related Entity Status (legacy)',
    configurator: UpdateRelatedEntityStatusLegacyConfigurator,
    display: UpdateRelatedEntityStatusLegacyDisplay,
    error: UpdateRelatedEntityStatusLegacyErrorPlugin,
    validate: (condition: Partial<WorkflowInvoke>) => {
      const data =
        condition.data as UpdateRelatedEntityStatusLegacyParams['data'];
      return !!(
        data &&
        data.entity &&
        (data.entity.id || data.entity.table) &&
        Array.isArray(data.relations) &&
        data.relations.length > 0 &&
        data.relations.every(relation => {
          return (
            relation.taskType &&
            Array.isArray(relation.events) &&
            relation.events.length > 0 &&
            relation.events.every(
              event => event.event && event.state && event.target
            ) &&
            Array.isArray(relation.propertyNames) &&
            relation.propertyNames.length > 0 &&
            relation.propertyNames.every(name => !!name)
          );
        })
      );
    },
    service: (
      state: State<TaskContext>,
      params:
        | UpdateRelatedEntityStatusLegacyParams
        | UpdateRelatedEntityStatusLegacyParams[]
    ) => {
      console.log('updateRelatedEntityStatusLegacy params', state, params);
      return true;
    }
  };
